import axios, { AxiosInstance, AxiosError } from 'axios';
import { authService } from './auth.service';
import { useStore } from './store';
import { mockApi } from './mock-api';
import type { PlayerData, ApiResponse, TransferDestination, TransferRequest } from '@/types/index';

class ApiService {
  private client: AxiosInstance;
  // Utiliser le mock si on est en dev OU si VITE_USE_MOCK_API est explicitement true
  private useMock = import.meta.env.DEV || import.meta.env.VITE_USE_MOCK_API === 'true';

  constructor() {
    this.client = axios.create({
      baseURL: '/api',
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Intercepteur pour ajouter les tokens d'authentification
    this.client.interceptors.request.use(
      (config) => {
        const token = authService.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Ajouter le player token depuis le store
        const playerToken = useStore.getState().playerToken;
        if (playerToken) {
          config.headers['X-Player-Token'] = playerToken;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Intercepteur pour gérer les erreurs
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          console.error('❌ Erreur 401: Token invalide ou API non disponible');
          authService.logout();
          // En mode dev/test, on ne reload pas pour éviter les boucles infinies
          // window.location.reload();
        }
        return Promise.reject(error);
      }
    );
  }

  // ===== PLAYER API =====

  /**
   * Récupère les données d'un joueur par son UUID
   */
  async getPlayer(uuid: string): Promise<PlayerData> {
    // Mode mock pour le développement
    if (this.useMock) {
      console.log('🔧 Using mock API for getPlayer');
      const response = await mockApi.getPlayer(uuid);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error || 'Failed to fetch player data');
    }

    // Mode production
    const response = await this.client.get<ApiResponse<PlayerData>>(
      `/players/${uuid}`
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to fetch player data');
  }

  /**
   * Récupère les données d'un joueur par son pseudo
   */
  async getPlayerByUsername(username: string): Promise<PlayerData> {
    // Mode mock pour le développement
    if (this.useMock) {
      console.log('🔧 Using mock API for getPlayerByUsername');
      const response = await mockApi.getPlayerByUsername(username);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.error || 'Failed to fetch player data');
    }

    // Mode production
    const response = await this.client.get<ApiResponse<PlayerData>>(
      `/players/username/${username}`
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to fetch player data');
  }

  // ===== TRANSFER API =====

  /**
   * Récupère la liste des destinations de transfert disponibles
   */
  async getTransferDestinations(): Promise<TransferDestination[]> {
    const response = await this.client.get<ApiResponse<TransferDestination[]>>(
      '/transfer/destinations'
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Failed to fetch transfer destinations');
  }

  /**
   * Transfère un ticket vers une destination
   */
  async transferTicket(request: TransferRequest): Promise<void> {
    const response = await this.client.post<ApiResponse>(
      '/transfer',
      request
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to transfer ticket');
    }
  }
}

export const apiService = new ApiService();
